import { MessageSquare, Heart, ShoppingBag, Briefcase, CreditCard, CheckCircle2, Clock } from 'lucide-react';
import { cn } from '../../../utils/helpers';
import { useTranslation } from '../../../hooks/useTranslation';
import { useLocale } from '../../../hooks/useLocale';

export default function Notifications() {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  const notifications = [
    { id: 1, title: t('notifications.newMessage'), content: t('notifications.messageContent', { name: 'Sarah Wilson', message: 'The payment was successful!' }), time: t('common.minutesAgo', { n: 2 }), read: false, icon: MessageSquare, color: 'text-blue-500 bg-blue-50 dark:bg-blue-950/40' },
    { id: 2, title: t('notifications.newLike'), content: t('notifications.likeContent', { name: 'Elena Gilbert', count: 12 }), time: t('common.minutesAgo', { n: 15 }), read: false, icon: Heart, color: 'text-rose-500 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40' },
    { id: 3, title: t('notifications.orderShipped'), content: t('notifications.orderContent', { order: '#12345', product: 'Premium Wireless Headphones' }), time: t('common.hoursAgo', { n: 1 }), read: true, icon: ShoppingBag, color: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/40' },
    { id: 4, title: t('notifications.applicationUpdate'), content: t('notifications.applicationContent', { company: 'TechFlow', role: 'Full Stack Engineer' }), time: t('common.hoursAgo', { n: 3 }), read: true, icon: Briefcase, color: 'text-amber-500 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40' },
    { id: 5, title: t('notifications.paymentReceived'), content: t('notifications.paymentContent', { amount: formatCurrency(450), name: 'Mike Ross' }), time: t('common.hoursAgo', { n: 5 }), read: true, icon: CreditCard, color: 'text-purple-500 bg-purple-50 dark:bg-purple-950/40' },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('notifications.title')}</h1>
          <p className="text-neutral-500 dark:text-neutral-400">{t('notifications.subtitle')}</p>
        </div>
        <button className="text-sm font-bold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">{t('notifications.markAllRead')}</button>
      </header>

      <div className="bg-white dark:bg-neutral-900 rounded-[2.5rem] border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm dark:shadow-none">
        {notifications.map((notif) => (
          <div 
            key={notif.id} 
            className={cn(
              "p-6 flex gap-4 border-b border-neutral-50 last:border-0 hover:bg-neutral-50/50 transition-colors cursor-pointer relative",
              !notif.read && "bg-neutral-50/30"
            )}
          >
            {!notif.read && (
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-neutral-900"></div>
            )}
            <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0", notif.color)}>
              <notif.icon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-start mb-1">
                <h3 className={cn("text-sm font-bold", !notif.read ? "text-neutral-900 dark:text-neutral-50" : "text-neutral-600 dark:text-neutral-300")}>
                  {notif.title}
                </h3>
                <span className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {notif.time}
                </span>
              </div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{notif.content}</p>
              <div className="mt-3 flex items-center gap-4">
                <button className="text-[10px] font-bold text-neutral-900 dark:text-neutral-50 uppercase tracking-widest hover:underline">{t('common.viewDetails')}</button>
                <button className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest hover:text-neutral-900 dark:hover:text-neutral-50">{t('common.dismiss')}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center">
        <button className="flex items-center gap-2 px-6 py-3 bg-neutral-100 dark:bg-neutral-800 rounded-2xl text-sm font-bold text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-all">
          <CheckCircle2 className="w-4 h-4" />
          {t('notifications.loadOlder')}
        </button>
      </div>
    </div>
  );
}
